import ExcelJS from "exceljs";
import dayjs from "dayjs";
import { message } from "antd";
import * as purchaseOrderService from "../services/purchaseOrderService";

// Minimal order shape to trigger export
type MinimalOrder = { id?: string; _id?: string; reference?: string };

export const exportPurchaseOrderToXLSX = (order: MinimalOrder) => {
  try {
    const orderId = order.id || order._id;
    if (!orderId) {
      message.error("Impossible de trouver l'identifiant du bon de commande");
      return;
    }

    purchaseOrderService
      .getPurchaseOrderById(orderId)
      .then(async (orderDetails: any) => {
        const da = orderDetails.deliveryAddress || {};
        const street = da.street || da.streetAddress || "";
        const cityLine = da.city ? `${da.city} ${da.postalCode || ""}` : "";
        const country = da.country || "";
        const createdAtStr = orderDetails.createdAt
          ? dayjs(orderDetails.createdAt).format("DD/MM/YYYY")
          : "";

        const workbook = new ExcelJS.Workbook();
        const ws = workbook.addWorksheet("Purchase Order");

        // 9 colonnes (A..I)
        ws.columns = [
          { header: "", key: "A", width: 50 },
          { header: "", key: "B", width: 22 },
          { header: "", key: "C", width: 10 },
          { header: "", key: "D", width: 14 },
          { header: "", key: "E", width: 18 },
          { header: "", key: "F", width: 16 },
          { header: "", key: "G", width: 20 },
          { header: "", key: "H", width: 14 },
          { header: "", key: "I", width: 18 },
        ];

        const applyBorder = (from: string, to: string) => {
          const [fromCol, fromRow] = [from.charCodeAt(0), parseInt(from.slice(1), 10)];
          const [toCol, toRow] = [to.charCodeAt(0), parseInt(to.slice(1), 10)];
          for (let r = fromRow; r <= toRow; r++) {
            for (let c = fromCol; c <= toCol; c++) {
              const addr = `${String.fromCharCode(c)}${r}`;
              const cell = ws.getCell(addr);
              cell.border = {
                top: { style: "thin", color: { argb: "FF000000" } },
                left: { style: "thin", color: { argb: "FF000000" } },
                bottom: { style: "thin", color: { argb: "FF000000" } },
                right: { style: "thin", color: { argb: "FF000000" } },
              } as any;
            }
          }
        };
        const eachCell = (from: string, to: string, fn: (cell: any) => void) => {
          const [fromCol, fromRow] = [from.charCodeAt(0), parseInt(from.slice(1), 10)];
          const [toCol, toRow] = [to.charCodeAt(0), parseInt(to.slice(1), 10)];
          for (let r = fromRow; r <= toRow; r++) {
            for (let c = fromCol; c <= toCol; c++) {
              fn(ws.getCell(`${String.fromCharCode(c)}${r}`));
            }
          }
        };

        // Titre
        ws.mergeCells("A1:I1");
        ws.getRow(1).height = 54;
        const titleCell = ws.getCell("A1");
        titleCell.value = "PURCHASE ORDER";
        titleCell.font = { name: "Arial", size: 40, bold: true };
        titleCell.alignment = { horizontal: "left", vertical: "middle" } as any;

        // Row 2 – Order Date
        ws.getCell("A2").value = "Order Date";
        ws.mergeCells("B2:D2");
        ws.getCell("B2").value = createdAtStr;

        // Row 3 – Buyer / Consignee
        ws.getCell("A3").value = "Buyer";
        ws.mergeCells("B3:D3");
        ws.getCell("B3").value = "Société ARGEMV SAS - Maison Kōsane";
        ws.getCell("E3").value = "Consignee";
        ws.mergeCells("F3:I3");
        ws.getCell("F3").value = da.name || ""; // fallback simple

        // Row 4 – SIREN / Shipping Address (Country)
        ws.getCell("A4").value = "SIREN / TVA Number";
        ws.mergeCells("B4:D4");
        ws.getCell("B4").value = "92505367000012 | FR43925053670";
        ws.mergeCells("E4:E5");
        ws.getCell("E4").value = "Shipping Address";
        ws.getCell("F4").value = "Country";
        ws.mergeCells("G4:I4");
        ws.getCell("G4").value = country;

        // Row 5 – Address (droite)
        ws.getCell("A5").value = "Address";
        ws.mergeCells("B5:D5");
        ws.getCell("B5").value = "3 Rue Blomet, 75015 PARIS";
        ws.getCell("F5").value = "Address";
        ws.mergeCells("G5:I5");
        ws.getCell("G5").value = [street, cityLine].filter(Boolean).join(", ");
        ws.getCell("G5").alignment = { wrapText: true, vertical: "top" } as any;

        // Row 6 – Email / Mandatory reference
        ws.getCell("A6").value = "Email";
        ws.mergeCells("B6:D6");
        ws.getCell("B6").value = { text: "contact@maisonkosane.com", hyperlink: "mailto:contact@maisonkosane.com" } as any;
        ws.getCell("B6").font = { name: "Arial", color: { argb: "FF0563C1" }, underline: true } as any;
        ws.getCell("F6").value = "Mandatory reference";
        ws.mergeCells("G6:I6");
        ws.getCell("G6").value = (da.comment || "").split(/\r?\n/).map((l: string) => `• ${l}`).join("\n");
        ws.getCell("G6").font = { name: "Arial", color: { argb: "FFFF0000" } } as any;
        ws.getCell("G6").alignment = { wrapText: true, vertical: "top" } as any;

        // Row 7 – Phone / Informations
        ws.getCell("A7").value = "Phone Number";
        ws.mergeCells("B7:D7");
        ws.getCell("B7").value = { text: "+33 (0)6 31 77 11 09", hyperlink: "tel:+33631771109" } as any;
        ws.getCell("B7").font = { name: "Arial", color: { argb: "FF0563C1" }, underline: true } as any;
        ws.getCell("F7").value = "Informations";
        ws.mergeCells("G7:I7");
        ws.getCell("G7").value = da.deliveryInfo || "";
        ws.getCell("G7").alignment = { wrapText: true, vertical: "top" } as any;

        // Labels en gras
        ["A2","A3","A4","A5","A6","A7","E3","E4","F4","F5","F6","F7"].forEach((addr) => {
          const c = ws.getCell(addr);
          c.font = { ...(c.font || {}), name: "Arial", bold: true } as any;
        });

        // Bordures zones
        applyBorder("A2", "D7");
        applyBorder("E2", "I7");

        // Ligne 2: enlever bordures internes B2:D2 (laisser séparateur A|B)
        const a2 = ws.getCell("A2");
        const b2 = ws.getCell("B2");
        const c2 = ws.getCell("C2");
        const d2 = ws.getCell("D2");
        a2.border = { ...(a2.border || {}), right: { style: "thin", color: { argb: "FF000000" } } } as any;
        b2.border = { top: { style: "thin", color: { argb: "FF000000" } }, left: { style: "thin", color: { argb: "FF000000" } }, bottom: { style: "thin", color: { argb: "FF000000" } }, right: { style: "thin", color: { argb: "FF000000" } } } as any;
        c2.border = { top: { style: "thin", color: { argb: "FF000000" } }, bottom: { style: "thin", color: { argb: "FF000000" } } } as any;
        d2.border = { top: { style: "thin", color: { argb: "FF000000" } }, bottom: { style: "thin", color: { argb: "FF000000" } } } as any;

        // Police et alignements zone info
        eachCell("A2", "I7", (cell) => {
          cell.font = { name: "Arial", size: 11 } as any;
          cell.alignment = { vertical: "middle", wrapText: true } as any;
        });

        // Hauteurs de lignes
        ws.getRow(2).height = 22;
        ws.getRow(3).height = 24;
        ws.getRow(4).height = 24;
        ws.getRow(5).height = 24;
        ws.getRow(6).height = 52;
        ws.getRow(7).height = 52;

        // Ligne vide
        ws.addRow([""]);

        // En-têtes produits
        const headerRowValues = new Array(9).fill("");
        headerRowValues[1] = "EAN";
        headerRowValues[2] = "BRAND";
        headerRowValues[3] = "HS CODE";
        headerRowValues[4] = "MADE IN";
        headerRowValues[5] = "UNIT PRICE";
        headerRowValues[6] = "UNIT WEIGHT (kg)";
        headerRowValues[7] = "ORDER QT";
        headerRowValues[8] = "TOTAL";
        const headerRow = ws.addRow(headerRowValues);
        headerRow.eachCell({ includeEmpty: true }, (cell: any) => {
          cell.font = { name: "Arial", bold: true };
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD9D9D9" } } as any;
          cell.border = { top: { style: "thin", color: { argb: "FF000000" } }, left: { style: "thin", color: { argb: "FF000000" } }, bottom: { style: "thin", color: { argb: "FF000000" } }, right: { style: "thin", color: { argb: "FF000000" } } } as any;
          cell.alignment = { horizontal: "center", vertical: "middle" } as any;
        });

        // Lignes produits
        let subtotal = 0;
        let totalWeight = 0;
        (orderDetails.items || []).forEach((item: any) => {
          const rowVals = new Array(9).fill("");
          const unitPrice = Number(item.unitPrice || 0);
          const qty = Number(item.quantity || 0);
          const weight = Number(item.unitWeightKg || 0);
          const total = Number(item.totalPrice || unitPrice * qty || 0);
          subtotal += total;
          totalWeight += weight * qty;

          rowVals[0] = (item.productName || "").toString();
          rowVals[1] = (item.barcode || "").toString();
          rowVals[2] = (item.vendor || "").toString();
          rowVals[3] = (item.hsCode || "").toString();
          rowVals[4] = (item.madeIn || "").toString();
          rowVals[5] = unitPrice ? unitPrice.toFixed(2) : "";
          rowVals[6] = weight ? weight.toFixed(3) : "";
          rowVals[7] = qty ? qty.toString() : "";
          rowVals[8] = total ? total.toFixed(2) : "";
          const r = ws.addRow(rowVals);
          r.eachCell({ includeEmpty: true }, (cell: any) => {
            cell.border = { top: { style: "thin", color: { argb: "FF000000" } }, left: { style: "thin", color: { argb: "FF000000" } }, bottom: { style: "thin", color: { argb: "FF000000" } }, right: { style: "thin", color: { argb: "FF000000" } } } as any;
            cell.font = { name: "Arial", size: 11 } as any;
          });
          r.getCell(6).numFmt = '€ #,##0.00';
          r.getCell(7).numFmt = '#,##0.000';
          r.getCell(9).numFmt = '€ #,##0.00';
          r.getCell(6).alignment = { horizontal: 'right' } as any;
          r.getCell(7).alignment = { horizontal: 'right' } as any;
          r.getCell(9).alignment = { horizontal: 'right' } as any;
        });

        // Totaux
        ws.addRow([""]);

        // Subtotal
        const subtotalRow = ws.addRow([
          "",
          "",
          "",
          "",
          "",
          "",
          "Subtotal (EUR)", // G
          "",          // H (sera mergé avec G)
          `${subtotal.toFixed(2).replace(".", ",")} €`,
        ]);

        // Estimated total weight
        const estWeightRow = ws.addRow([
          "",
          "",
          "",
          "",
          "",
          "",
          "Estimated total weight (kg)", // G
          "",                        // H
          totalWeight
            ? `${totalWeight.toFixed(2).replace(".", ",")} kg`
            : "",
        ]);

        // Delivery / import cost line: importCost prioritaire, sinon estimatedCost
        const importCost = Number(orderDetails.importCost || 0);
        const estimatedCost = Number(orderDetails.estimatedCost || 0);
        const hasImportCost = importCost > 0;
        const hasEstimatedCost = !hasImportCost && estimatedCost > 0;
        const baseDeliveryLabel = hasImportCost ? "Import cost" : "Estimated delivery charges";
        const deliveryLabel = `${baseDeliveryLabel} (EUR)`;
        const deliveryAmount = hasImportCost
          ? importCost
          : hasEstimatedCost
          ? estimatedCost
          : 0;

        const shipRow = ws.addRow([
          "",
          "",
          "",
          "",
          "",
          "",
          deliveryLabel, // G
          "",            // H
          deliveryAmount
            ? `${deliveryAmount.toFixed(2).replace(".", ",")} €`
            : "",
        ]);

        // VAT line (pas de TVA connue -> "-")
        const vatRow = ws.addRow([
          "",
          "",
          "",
          "",
          "",
          "",
          "VAT (EUR)", // G
          "",    // H
          "-",
        ]);

        // Total général = subtotal + livraison (import ou estimée)
        const grandTotal = subtotal + deliveryAmount;
        const totalRow = ws.addRow([
          "",
          "",
          "",
          "",
          "",
          "",
          "Total (EUR)", // G
          "",      // H
          `${grandTotal.toFixed(2).replace(".", ",")} €`,
        ]);

        // Fusionner G:H pour les libellés afin d'éviter qu'ils soient coupés
        [subtotalRow, estWeightRow, shipRow, vatRow, totalRow].forEach((r) => {
          ws.mergeCells(`G${r.number}:H${r.number}`);
        });

        // Style spécifique pour la ligne de poids estimé
        estWeightRow.getCell(7).font = {
          name: "Arial",
          size: 11,
          italic: true,
          color: { argb: "FF808080" },
        } as any;
        estWeightRow.getCell(9).font = {
          name: "Arial",
          size: 11,
          italic: true,
          color: { argb: "FF808080" },
        } as any;

        // Appliquer alignement et polices aux lignes de totaux
        const totalRows = [subtotalRow, estWeightRow, shipRow, vatRow, totalRow];
        totalRows.forEach((r) => {
          const labelCell = r.getCell(7); // G (fusionné avec H)
          const valueCell = r.getCell(9); // I

          labelCell.alignment = { horizontal: "right" } as any;
          valueCell.alignment = { horizontal: "right" } as any;

          if (!labelCell.font) {
            labelCell.font = { name: "Arial", size: 11 } as any;
          }
          if (!valueCell.font) {
            valueCell.font = { name: "Arial", size: 11 } as any;
          }
        });

        // Formats numériques / unités
        subtotalRow.getCell(9).numFmt = '€ #,##0.00';
        shipRow.getCell(9).numFmt = '€ #,##0.00';
        estWeightRow.getCell(9).numFmt = '#,##0.00 "kg"';
        totalRow.getCell(9).numFmt = '€ #,##0.00';

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        const ref = orderDetails.reference || order.reference || orderId;
        link.href = url;
        link.download = `commande_${ref}_${dayjs().format("YYYYMMDD")}\.xlsx`;
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        message.success("Export XLSX réussi");
      })
      .catch((error) => {
        console.error("Erreur lors de l'export XLSX:", error);
        message.error("Impossible d'exporter le bon de commande (XLSX)");
      });
  } catch (error) {
    console.error("Erreur lors de l'export XLSX:", error);
    message.error("Une erreur s'est produite lors de l'export XLSX");
  }
};
